import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { tokenStore } from '../../common/services/tokenStore';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Avatar,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { renderImagePath } from '../../components/Chat/GuidelineSources/helpers';
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TopToolbar from '../../components/TopToolbar';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import * as S from './style';

interface GuidelineItem {
  organisation_guideline_id: string;
  guideline_docs_id: string;
  title: string;
  description: string;
  url: string;
  source_org: string;
  language: string;
  image_url?: string;
  is_active: boolean;
  added_at: string;
}

interface OrgGuidelinesResponse {
  organisation_id: string;
  organisation_name: string;
  guidelines: GuidelineItem[];
}

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  no: 'NO',
};

const Guidelines: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState<OrgGuidelinesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_BASE_URL || (import.meta as any).env?.VITE_API_URL || 'http://localhost:1245';

  useEffect(() => {
    if (!user) return;
    const token = tokenStore.get();
    axios
      .get<OrgGuidelinesResponse>(`${apiUrl}/api/v1/organisations/me/guidelines`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setData(res.data))
      .catch(() => setError(t('guidelines.error')))
      .finally(() => setLoading(false));
  }, [user, apiUrl]);

  const handleRowClick = (url: string) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <S.PageContainer>
      <TopToolbar />
      <S.ContentWrapper>
        {/* Header */}
        <S.Header>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <MenuBookIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
            <Typography variant="h5" fontWeight={700}>
              {t('guidelines.title')}
            </Typography>
          </Box>
          {data && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {data.organisation_name}
              </Typography>
              <S.OrgBadge
                label={t(
                  data.guidelines.length === 1 ? 'guidelines.count_one' : 'guidelines.count_other',
                  { count: data.guidelines.length }
                )}
                size="small"
              />
            </Box>
          )}
        </S.Header>

        {/* States */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress aria-label={t('guidelines.loading')} />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {!loading && !error && data && data.guidelines.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <MenuBookIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('guidelines.empty')}
            </Typography>
          </Box>
        )}

        {/* Table */}
        {!loading && !error && data && data.guidelines.length > 0 && (
          <S.TablePaper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 40 }} />
                    <TableCell sx={{ fontWeight: 700 }}>{t('guidelines.col.title')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('guidelines.col.description')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('guidelines.col.source')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 70 }}>{t('guidelines.col.lang')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.guidelines.map((g) => (
                    <S.ClickableRow
                      key={g.organisation_guideline_id}
                      onClick={() => handleRowClick(g.url)}
                    >
                      {/* Logo / icon */}
                      <TableCell sx={{ py: 1.5 }}>
                        {g.image_url ? (
                          <Box
                            component="img"
                            src={renderImagePath(g.image_url) || ''}
                            alt={g.source_org}
                            sx={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 40, height: 40 }}>
                            {g.source_org.charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                      </TableCell>

                      {/* Title */}
                      <TableCell sx={{ py: 1.5, maxWidth: 260 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {g.title}
                        </Typography>
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ py: 1.5, maxWidth: 340 }}>
                        <S.DescriptionText>
                          <Typography variant="caption" color="text.secondary" component="span">
                            {g.description}
                          </Typography>
                        </S.DescriptionText>
                      </TableCell>

                      {/* Source org */}
                      <TableCell sx={{ py: 1.5, maxWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {g.source_org}
                        </Typography>
                      </TableCell>

                      {/* Language */}
                      <TableCell sx={{ py: 1.5 }}>
                        <S.LanguageChip
                          label={LANGUAGE_LABELS[g.language] ?? g.language.toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Open icon */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Tooltip title={t('guidelines.open')}>
                          <OpenInNewIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </Tooltip>
                      </TableCell>
                    </S.ClickableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </S.TablePaper>
        )}
      </S.ContentWrapper>
    </S.PageContainer>
  );
};

export default Guidelines;
